/**
 * The real mixer.
 *
 * Two rules shape this class:
 *
 *   1. **Nothing has to be loaded.** Effects are synthesised (synth.ts) and
 *      lines are spoken by the browser (Voice.ts), so the game makes noise the
 *      moment the page is open. Recorded samples (samples.ts) override single
 *      cues on top of that; anything not recorded, or not downloaded yet,
 *      still comes out of the synthesiser.
 *   2. **Nothing happens before a gesture.** Browsers refuse to start an
 *      AudioContext until the user has interacted with the page, and a context
 *      created too early is stuck in `suspended` forever. So the context is
 *      built lazily on the first click or keypress - which in practice is the
 *      same click that grabs pointer lock.
 *
 * Sounds asked for before that gesture are dropped; loops asked for before it
 * are remembered and start as soon as there is somewhere to play them.
 */

import {
  AUDIO_SEED,
  KARAOKE,
  MIX,
  MUURIKKA_SIHINA,
  RUN_LOOP,
  SAMPLES,
  TELTTA_DJ,
  YARD,
} from '../config/audio';
import { mulberry32 } from '../utils/prng';
import {
  playOneShot,
  type Voicing,
} from './synth';
import {
  makeLoopBuffer,
  makeNoiseBuffer,
} from './buffers';
import { Voice } from './Voice';
import { karaokeMix, SILENT_MIX, type ListenerMix } from './karaoke';
import { runMix, STILL_MIX, type RunMix } from './pace';
import { yardMix, UNPLACED_MIX } from './yard';
import { telttaMix, TELTTA_SILENT_MIX } from './teltta';
import { muurikkaMix, MUURIKKA_SILENT_MIX } from './muurikka';
import { SampleBank } from './SampleBank';
import { allSamples, unknownSamples, type SampleId } from './samples';
import type {
  AudioManager,
  LoopSound,
  OneShotSound,
  SampleStatus,
  SpokenLine,
} from './AudioManager';

const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;

/**
 * Close enough that re-scheduling the ramp would be pure waste. `setListener`
 * runs once per rendered frame, and `setTargetAtTime` keeps converging on its
 * last target on its own, so a listener that has not really moved costs
 * nothing at all.
 */
function settled(next: ListenerMix, current: ListenerMix): boolean {
  return Math.abs(next.gain - current.gain) < 0.002 && Math.abs(next.cutoff - current.cutoff) < 20;
}

/**
 * How a loop is put in the world, for the loops that are in it at all.
 *
 * All three kinds get the same graph - a lowpass in front of a gain, both
 * following `setListener` - and differ only in which sum decides where they
 * land:
 *
 *   * `source` is a thing standing in a room, and the room is somewhere else.
 *     Distance and a wall, i.e. `karaokeMix`.
 *   * `room` is the room itself, and you are in it or you are not. No distance
 *     to speak of, just the wall, i.e. `yardMix`.
 *   * `teltta` is a thing standing out in the open a hundred metres off, which
 *     is neither: distance takes the *top* off it long before it takes the
 *     level, i.e. `telttaMix`.
 *   * `muurikka` is a thing standing out in the open a stride away, which is
 *     the other end of that: it has no bottom to carry with, so it is simply
 *     over at eighteen metres, i.e. `muurikkaMix`.
 *
 * A loop named by none of them is ambience with no place at all, and plays at
 * one level wherever you happen to be standing.
 */
type Placement = 'source' | 'room' | 'teltta' | 'muurikka';

const PLACEMENTS: ReadonlyMap<LoopSound, Placement> = new Map<LoopSound, Placement>([
  ['karaoke', 'source'],
  // The yard, heard from indoors through whichever wall you are behind.
  ['ambient-morning', 'room'],
  // The PA in the XLMOTO tent, heard from everywhere.
  ['teltta-dj', 'teltta'],
  // The pan on the kivetys, heard from the last few metres of the approach to
  // it and nowhere else.
  ['muurikka', 'muurikka'],
]);

/** Seconds for a placement to catch up with the listener, by kind. */
const PLACEMENT_SMOOTHING: Record<Placement, number> = {
  source: KARAOKE.smoothing,
  room: YARD.smoothing,
  teltta: TELTTA_DJ.smoothing,
  muurikka: MUURIKKA_SIHINA.smoothing,
};

/**
 * Loops that are the player's own movement. These follow `setPace` instead of
 * `setListener`: their level *and* their playback rate come from how fast the
 * run is going, which is the one thing a fixed-length recording cannot know.
 */
const PACED_LOOPS: ReadonlySet<LoopSound> = new Set<LoopSound>(['running']);

interface PlayingLoop {
  source: AudioBufferSourceNode;
  gain: GainNode;
  /** Only on a placed loop: the wall between the listener and the sound. */
  filter: BiquadFilterNode | null;
  /** Which of the two sums that filter and its gain are following. */
  placement: Placement | null;
  /** Only on a paced loop: it is resampled rather than filtered. */
  paced: boolean;
}

export class WebAudioManager implements AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private readonly buffers = new Map<LoopSound, AudioBuffer>();
  private readonly playing = new Map<LoopSound, PlayingLoop>();
  /** Loops the game wants running; the real ones are reconciled against it. */
  private readonly wanted = new Set<LoopSound>();
  /** Last position rendering reported, and what it works out to per placement. */
  private readonly mixes: Record<Placement, ListenerMix> = {
    source: SILENT_MIX,
    room: UNPLACED_MIX,
    teltta: TELTTA_SILENT_MIX,
    muurikka: MUURIKKA_SILENT_MIX,
  };
  /** Last ground speed rendering reported, and what it works out to. */
  private pace: RunMix = STILL_MIX;

  private enabled = true;
  private suspended = false;
  private disposed = false;

  /**
   * Presentation-only randomness. Seeded rather than Math.random so that two
   * identical runs sound identical, the same way they look identical - see the
   * determinism rules in README.md. It never touches the simulation.
   */
  private readonly rng = mulberry32(AUDIO_SEED);
  private readonly voice = new Voice();
  private readonly samples = new SampleBank(allSamples());

  /**
   * The recorded line currently in the air, so asking for it again does not
   * restart it and stutter - the same rule `Voice.say` applies to TTS.
   */
  private spokenSample: { id: string; endsAt: number; source: AudioBufferSourceNode } | null = null;

  constructor() {
    for (const event of UNLOCK_EVENTS) {
      window.addEventListener(event, this.onUserGesture, { passive: true });
    }
    // Downloading needs no gesture, so it starts now - by the time anybody has
    // clicked, read the READY screen and pressed W, the bytes are here and the
    // only thing left to do on the frame a cue fires is start a buffer.
    this.samples.preload();

    const strays = unknownSamples();
    if (strays.length > 0) {
      console.warn(
        `[laniaamu] these sample files match no cue and will never play:\n  ${strays.join('\n  ')}`,
      );
    }
  }

  /* --------------------------------------------------------------- events -- */

  play(sound: OneShotSound, volume = 1): void {
    const voicing = this.voicing();
    if (!voicing) return;
    // A recording of this cue wins; with none, nothing was consumed from the
    // PRNG and the synthesised version is bit-for-bit what it always was.
    const buffer = this.samples.get(sound, this.rng);
    if (buffer) this.playSample(voicing, buffer, sound, MIX.gain[sound] * volume);
    else playOneShot(voicing, sound, volume);
  }

  /**
   * A line, said out loud. A recording of it beats the browser's voice, and
   * unlike the browser's voice it goes through the mixer - so it obeys the
   * master gain, mutes with everything else and freezes on pause.
   */
  speak(line: SpokenLine): void {
    if (!this.enabled || this.suspended) return;

    const voicing = this.voicing();
    const id: SampleId = `voice:${line.id}`;
    const buffer = voicing ? this.samples.get(id, this.rng) : null;
    if (voicing && buffer) {
      if (this.spokenSample?.id === line.id && voicing.ctx.currentTime < this.spokenSample.endsAt) {
        return;
      }
      this.voice.cancel();
      const gain = SAMPLES.voiceGain * (line.volume ?? 1);
      const source = this.playSample(voicing, buffer, id, gain);
      if (source) {
        this.spokenSample = {
          id: line.id,
          endsAt: voicing.ctx.currentTime + buffer.duration,
          source,
        };
      }
      return;
    }

    const cue = line.cue;
    this.voice.say(line, cue ? () => this.play(cue, line.volume ?? 1) : undefined);
  }

  isSpeaking(id: string): boolean {
    if (!this.enabled) return false;
    return this.voice.isSpeaking(id) ||
      (this.spokenSample?.id === id &&
        (this.ctx?.currentTime ?? Infinity) < this.spokenSample.endsAt);
  }

  startLoop(sound: LoopSound): void {
    this.wanted.add(sound);
    this.syncLoops();
  }

  stopLoop(sound: LoopSound): void {
    this.wanted.delete(sound);
    this.syncLoops();
  }

  /**
   * Called once per rendered frame, so it does as little as it can get away
   * with: the arithmetic is four hypots, a clamp and a handful of box tests,
   * and the graph is not touched at all until one of the four answers has
   * actually moved. All four are computed every frame because any one of them
   * can move on its own - the navetta's front door changes all four at once,
   * but the yard between the tent and the tub changes only the tent, and the
   * pirtti's doorway changes the birds and the tent and not the karaoke.
   */
  setListener(x: number, z: number): void {
    const source = karaokeMix(x, z);
    const room = yardMix(x, z);
    const teltta = telttaMix(x, z);
    const muurikka = muurikkaMix(x, z);
    if (
      settled(source, this.mixes.source) &&
      settled(room, this.mixes.room) &&
      settled(teltta, this.mixes.teltta) &&
      settled(muurikka, this.mixes.muurikka)
    ) {
      return;
    }
    this.mixes.source = source;
    this.mixes.room = room;
    this.mixes.teltta = teltta;
    this.mixes.muurikka = muurikka;
    this.applyPlacement();
  }

  setPace(speed: number): void {
    const next = runMix(speed);
    // Called every frame from rendering, so the same guard the listener gets:
    // re-scheduling an identical ramp sixty times a second is pure waste.
    if (Math.abs(next.gain - this.pace.gain) < 0.002 && Math.abs(next.rate - this.pace.rate) < 0.004) {
      return;
    }
    this.pace = next;
    this.applyPace();
  }

  stopAll(): void {
    this.wanted.clear();
    this.syncLoops();
    this.stopSpeech();
  }

  stopSpeech(): void {
    this.voice.cancel();
    this.stopSpokenSample();
  }

  /* -------------------------------------------------------------- control -- */

  setEnabled(enabled: boolean): void {
    if (enabled === this.enabled) return;
    this.enabled = enabled;
    if (!enabled) this.voice.cancel();
    // Turning audio back on happens inside a click on the settings checkbox,
    // which is a user gesture - so this is a legal moment to open the context.
    else this.unlock();
    this.applyMasterGain();
    this.syncLoops();
  }

  /**
   * Freezes the mixer where it stands. The AudioContext keeps its clock and its
   * node graph, so an ambience loop resumes mid-breath instead of restarting.
   */
  setSuspended(suspended: boolean): void {
    if (suspended === this.suspended) return;
    this.suspended = suspended;
    this.voice.setSuspended(suspended);
    if (!this.ctx) return;
    if (suspended) void this.ctx.suspend();
    else void this.ctx.resume();
  }

  sampleStatus(): SampleStatus {
    return this.samples.status();
  }

  dispose(): void {
    this.disposed = true;
    this.detachUnlock();
    this.stopAll();
    this.voice.dispose();
    this.samples.dispose();
    void this.ctx?.close();
    this.ctx = null;
    this.master = null;
  }

  /**
   * Opens the AudioContext, or resumes one the browser put back to sleep.
   * Safe to call as often as you like; only the first call does any work.
   */
  unlock(): void {
    if (this.disposed || !this.enabled) return;

    if (!this.ctx) {
      const ctx = new AudioContext();
      const master = ctx.createGain();
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;
      this.noise = makeNoiseBuffer(ctx, this.rng);
      this.applyMasterGain();
      // First legal moment to decode: this is the gesture the browser wanted.
      // Loops are reconciled again afterwards, because a loop whose recording
      // had not landed yet - the karaoke, which has no synthesised stand-in -
      // is not playing and is waiting for exactly this.
      void this.samples.attach(ctx).then(() => this.syncLoops());
    }

    // Chrome also suspends the context on its own when a tab is backgrounded.
    if (!this.suspended && this.ctx.state !== 'running') void this.ctx.resume();
    this.syncLoops();
  }

  /* --------------------------------------------------------------- internal */

  private voicing(): Voicing | null {
    if (!this.enabled || this.suspended) return null;
    const { ctx, master, noise } = this;
    if (!ctx || !master || !noise) return null;
    return { ctx, out: master, noise, rng: this.rng };
  }

  /**
   * Fires a decoded buffer through the mixer. `trimStart` skips the head of the
   * file, which is how an mp3's encoder padding is dialled back out so the cue
   * lands on the frame that asked for it rather than 20 ms behind it.
   */
  private playSample(
    v: Voicing,
    buffer: AudioBuffer,
    id: SampleId,
    gain: number,
  ): AudioBufferSourceNode | null {
    const trim = SAMPLES.per[id] ?? {};
    const offset = Math.min(Math.max(trim.trimStart ?? 0, 0), buffer.duration);
    const source = v.ctx.createBufferSource();
    source.buffer = buffer;
    const level = v.ctx.createGain();
    level.gain.value = gain * SAMPLES.master * (trim.gain ?? 1);
    source.connect(level).connect(v.out);
    source.start(v.ctx.currentTime, offset);
    return source;
  }

  private stopSpokenSample(): void {
    if (!this.spokenSample) return;
    try {
      this.spokenSample.source.stop();
    } catch {
      // Already finished on its own; nothing to stop.
    }
    this.spokenSample = null;
  }

  private applyMasterGain(): void {
    if (!this.master || !this.ctx) return;
    const target = this.enabled ? MIX.master : 0;
    this.master.gain.setTargetAtTime(target, this.ctx.currentTime, 0.02);
  }

  /** Brings the loops that are actually playing in line with `wanted`. */
  private syncLoops(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;

    const shouldPlay = this.enabled ? this.wanted : new Set<LoopSound>();

    for (const [sound, node] of this.playing) {
      if (shouldPlay.has(sound)) continue;
      const stopAt = ctx.currentTime + MIX.loopFade;
      node.gain.gain.cancelScheduledValues(ctx.currentTime);
      node.gain.gain.setValueAtTime(node.gain.gain.value, ctx.currentTime);
      node.gain.gain.linearRampToValueAtTime(0, stopAt);
      node.source.stop(stopAt);
      this.playing.delete(sound);
    }

    for (const sound of shouldPlay) {
      if (this.playing.has(sound)) continue;
      const buffer = this.loopBuffer(ctx, sound);
      // Nothing to play with yet. A recorded-only loop waits for its file
      // rather than being faked, and `unlock` re-runs this once it lands.
      if (!buffer) continue;

      const now = ctx.currentTime;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);

      const placement = PLACEMENTS.get(sound);
      if (placement) {
        // Straight to the wall the listener is behind rather than sweeping up
        // to it: the run starts 130 m from the karaoke and indoors, and the
        // first thing anybody should hear of either is the muffled version.
        const mix = this.mixes[placement];
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.Q.value = KARAOKE.resonance;
        filter.frequency.setValueAtTime(mix.cutoff, now);
        gain.gain.setTargetAtTime(MIX.gain[sound] * mix.gain, now, PLACEMENT_SMOOTHING[placement]);
        source.connect(filter).connect(gain).connect(master);
        source.start();
        this.playing.set(sound, { source, gain, filter, placement, paced: false });
        continue;
      }

      if (PACED_LOOPS.has(sound)) {
        // Straight to the pace the player is already going, rather than fading
        // up over `loopFade`: this loop starts when the run starts, and a run
        // that is already at speed should not spend a second catching up.
        gain.gain.setTargetAtTime(MIX.gain[sound] * this.pace.gain, now, RUN_LOOP.smoothing);
        source.playbackRate.setValueAtTime(this.pace.rate, now);
        source.connect(gain).connect(master);
        source.start();
        this.playing.set(sound, { source, gain, filter: null, placement: null, paced: true });
        continue;
      }

      gain.gain.linearRampToValueAtTime(MIX.gain[sound], now + MIX.loopFade);
      source.connect(gain).connect(master);
      source.start();
      this.playing.set(sound, { source, gain, filter: null, placement: null, paced: false });
    }
  }

  /**
   * A recording of the loop if one has arrived, and the synthesised version
   * otherwise. The synthesised buffer is built once and kept; a recording that
   * lands after its loop is already playing does not interrupt it, because
   * swapping the buffer mid-loop is a click for no gain.
   */
  private loopBuffer(ctx: AudioContext, sound: LoopSound): AudioBuffer | null {
    const recorded = this.samples.get(sound, this.rng);
    if (recorded) return recorded;

    // Still downloading: wait for it rather than start on the stand-in. The
    // synthesised version is the fallback for an empty samples folder, not for
    // a slow connection, and because a loop keeps whatever buffer it started
    // with, faking the first second here means faking the whole run.
    if (this.samples.pending(sound)) return null;

    const existing = this.buffers.get(sound);
    if (existing) return existing;
    const made = makeLoopBuffer(ctx, sound, this.rng);
    if (made) this.buffers.set(sound, made);
    return made;
  }

  /** Puts every paced loop at the speed the player has got to. */
  private applyPace(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    for (const [sound, node] of this.playing) {
      if (!node.paced) continue;
      const now = ctx.currentTime;
      node.gain.gain.setTargetAtTime(MIX.gain[sound] * this.pace.gain, now, RUN_LOOP.smoothing);
      node.source.playbackRate.setTargetAtTime(this.pace.rate, now, RUN_LOOP.smoothing);
    }
  }

  /** Points every placed loop at where the listener has got to. */
  private applyPlacement(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    for (const [sound, node] of this.playing) {
      if (!node.filter || !node.placement) continue;
      const now = ctx.currentTime;
      const mix = this.mixes[node.placement];
      const smoothing = PLACEMENT_SMOOTHING[node.placement];
      node.gain.gain.setTargetAtTime(MIX.gain[sound] * mix.gain, now, smoothing);
      node.filter.frequency.setTargetAtTime(mix.cutoff, now, smoothing);
    }
  }

  private detachUnlock(): void {
    for (const event of UNLOCK_EVENTS) {
      window.removeEventListener(event, this.onUserGesture);
    }
  }

  private readonly onUserGesture = (): void => {
    this.unlock();
    // One gesture is all it takes; a live context survives on its own.
    if (this.ctx?.state === 'running') this.detachUnlock();
  };
}
